// Reads versions.md for the "wat is nieuw" modal in the portal shell (I5618).
// A version starts with "{versienummer} 1.7.27 - {versie datum} 2026-11-04". Below it come
// "## Section" lines, "**Title**" lines and "- bullet" lines, with nested bullets indented.

const VERSION_LINE = /^\{versienummer\}\s*(\S+)\s*-\s*\{versie datum\}\s*(.*?)\s*$/;
const SECTION_LINE = /^#{1,6}\s+(.+?)\s*$/;
const TITLE_LINE = /^\*\*((?:(?!\*\*).)+)\*\*:?\s*$/;
const BULLET_LINE = /^(\s*)[-*]\s+(.+?)\s*$/;
const SEPARATOR_LINE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

const DUTCH_MONTHS = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

const plainText = (text) =>
  text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\*\*/g, '')
    .replace(/\*(\S(?:[^*]*\S)?)\*/g, '$1')
    .trim();

/**
 * @param {string} raw content of versions.md
 * @param {number} limit how many versions to keep, newest first as in the file
 * @returns {Array<{version: string, date: string, items: Array<{section: string|null,
 *   title: string|null, lines: Array<{text: string, nested: boolean}>}>}>}
 *   an empty array when the file is empty or holds no version line
 */
export function parseVersionLog(raw, limit = 5) {
  if (typeof raw !== 'string') return [];

  const versions = [];
  let current = null;
  let section = null;
  let item = null;

  const openItem = (title) => {
    item = { section, title, lines: [] };
    section = null;
    current.items.push(item);
    return item;
  };

  for (const line of raw.split(/\r?\n/)) {
    const versionMatch = line.match(VERSION_LINE);
    if (versionMatch) {
      if (versions.length === limit) break;
      current = { version: versionMatch[1], date: versionMatch[2], items: [] };
      versions.push(current);
      section = null;
      item = null;
      continue;
    }
    if (!current || !line.trim() || SEPARATOR_LINE.test(line)) continue;

    const sectionMatch = line.match(SECTION_LINE);
    if (sectionMatch) {
      section = plainText(sectionMatch[1]);
      item = null;
      continue;
    }
    const titleMatch = line.match(TITLE_LINE);
    if (titleMatch) {
      openItem(plainText(titleMatch[1]));
      continue;
    }
    const bulletMatch = line.match(BULLET_LINE);
    const target = item || openItem(null);
    target.lines.push({
      text: plainText(bulletMatch ? bulletMatch[2] : line),
      nested: Boolean(bulletMatch && bulletMatch[1].length > 0),
    });
  }

  return versions;
}

/** "2026-11-04" becomes "4 november 2026"; anything else stays as it is in the file. */
export function formatVersionDate(date) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const month = match && DUTCH_MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month} ${match[1]}` : date || '';
}
